/**
 * Error handling utilities for extracting user-friendly error messages
 * from various error types (GraphQL, API, generic).
 */

export interface GraphQLErrorLike {
  response?: {
    status?: number
    errors?: Array<{
      message?: string
      extensions?: {
        code?: string
      }
    }>
  }
  request?: {
    query?: string
    variables?: unknown
  }
  message?: string
}

function hasAuthErrorCode(error: unknown, code: string): boolean {
  const graphQLError = error as GraphQLErrorLike
  return (
    graphQLError?.response?.errors?.some(
      graphQLErrorItem => graphQLErrorItem.extensions?.code === code
    ) ?? false
  )
}

function getNormalizedErrorMessage(error: unknown): string {
  return (
    getGraphQLErrorMessage(error) ||
    (error as GraphQLErrorLike)?.message ||
    ''
  ).toLowerCase()
}

function getResponseStatus(error: unknown): number | null {
  const graphQLError = error as GraphQLErrorLike
  return typeof graphQLError?.response?.status === 'number'
    ? graphQLError.response.status
    : null
}

function getErrorMessage(error: unknown): string {
  return (error as GraphQLErrorLike)?.message || ''
}

function getGraphQLTransportCode(error: unknown): number | null {
  const match = getErrorMessage(error).match(/GraphQL Error \(Code: (\d{3})\)/)
  if (!match) return null

  const code = Number(match[1])
  return Number.isInteger(code) ? code : null
}

function isServerResponseFailure(error: unknown): boolean {
  const status = getResponseStatus(error) ?? getGraphQLTransportCode(error)
  return typeof status === 'number' && status >= 500
}

function isLikelyHtmlErrorMessage(error: unknown): boolean {
  return /<!doctype html|<html[\s>]/i.test(getErrorMessage(error))
}

export function isAuthenticationFailure(error: unknown): boolean {
  return hasAuthErrorCode(error, 'AUTH_NOT_AUTHENTICATED')
}

export function isAuthorizationFailure(error: unknown): boolean {
  if (hasAuthErrorCode(error, 'AUTH_NOT_AUTHORIZED')) {
    return true
  }

  const status = getResponseStatus(error)
  if (status === 403) {
    return true
  }

  const message = (
    getGraphQLErrorMessage(error) ||
    (error as GraphQLErrorLike)?.message ||
    ''
  ).toLowerCase()

  return message.includes('not authorized')
}

function isAuthenticationRelatedMessage(error: unknown): boolean {
  const message = getNormalizedErrorMessage(error)

  return (
    message.includes('not authenticated') ||
    message.includes('authentication failed') ||
    message.includes('unauthorized')
  )
}

function getGraphQLErrorMessage(error: unknown): string | null {
  const graphQLError = error as GraphQLErrorLike
  return graphQLError?.response?.errors?.[0]?.message || null
}

export function getUserFacingErrorMessage(
  error: unknown,
  fallback: string
): string {
  if (isAuthenticationFailure(error) || isAuthenticationRelatedMessage(error)) {
    return 'Your session is no longer valid. Please sign in again and try again.'
  }

  if (isAuthorizationFailure(error)) {
    return 'You do not have permission to access this resource.'
  }

  const graphQLErrorMessage = getGraphQLErrorMessage(error)
  if (graphQLErrorMessage) {
    return graphQLErrorMessage
  }

  if (isServerResponseFailure(error) || isLikelyHtmlErrorMessage(error)) {
    return fallback
  }

  const graphQLError = error as GraphQLErrorLike
  if (graphQLError?.message) {
    return graphQLError.message
  }

  return fallback
}

export class UserFacingGraphQLError extends Error {
  response?: GraphQLErrorLike['response']
  request?: GraphQLErrorLike['request']
  authenticationFailure: boolean
  authorizationFailure: boolean

  constructor(error: unknown, fallback: string) {
    super(getUserFacingErrorMessage(error, fallback))
    this.name = 'UserFacingGraphQLError'

    const graphQLError = error as GraphQLErrorLike
    this.response = graphQLError?.response
    this.request = graphQLError?.request
    this.authenticationFailure = isAuthenticationFailure(error)
    this.authorizationFailure = isAuthorizationFailure(error)
  }
}

/**
 * Extracts a user-friendly error message from an unknown error.
 * Handles GraphQL errors, API errors, and generic Error objects.
 * @param error - The error to extract a message from
 * @param fallback - Default message if no specific message found
 * @returns User-friendly error message
 */
export function extractErrorMessage(error: unknown, fallback: string): string {
  return getUserFacingErrorMessage(error, fallback)
}
